import { useEffect, useMemo, useState } from 'react';

import { useGetDashboardDataByWorkspace } from '@/api/dashboard/useDashboardDetailsByWorkspace';
import DashboardLoader from '@/component-library/primitives/Loaders/DashboardLoader';
import { useStripeFinancialFeaturesEnabled } from '@/hooks/useStripeFinancialFeaturesEnabled';
import { usePageTracking } from '@/hooks/useTracking';
import { DashboardData } from '@/models/DashboardData';

import { AlertPanel } from './Components/AlertPanel';
import { TrackedActivityPanel } from './Components/TrackedActivityPanel';
import {
  ChurnRiskSegments,
  CustomCard,
  MonthlyChurnRate,
  MonthlyRecurringRevenue,
  NetSubscriptionChange,
  RevenueByPlan,
} from './Components/MetricComponents';
import { normalizeSignalStatus } from '@/utils/signalStatus';

import SetupSteps from './SetupSteps';

const DashboardPage = () => {
  const [timePeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  // Track page view
  usePageTracking('dashboard', { timePeriod });

  // Setup state - in real app, get this from user context or workspace settings
  const [setupComplete, setSetupComplete] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false); // Replace with actual check

  // Calculate date range based on selected time period (memoized to prevent infinite loops)
  const { startTime, endTime } = useMemo(() => {
    const now = new Date();
    const endDate = new Date(now);
    const startDate = new Date(now);

    switch (timePeriod) {
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(now.getDate() - 90);
        break;
      case '180d':
        startDate.setDate(now.getDate() - 180);
        break;
      case '365d':
        startDate.setDate(now.getDate() - 365);
        break;
      default:
        startDate.setDate(now.getDate() - 30);
    }

    // Use fixed times to prevent constant changes
    startDate.setHours(0, 0, 0, 0); // Start of day
    endDate.setHours(23, 59, 59, 999); // End of day

    return {
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
    };
  }, [timePeriod]);
  const { data, isLoading: queryLoading } = useGetDashboardDataByWorkspace(startTime, endTime);
  const workspaceStripe = useStripeFinancialFeaturesEnabled();

  useEffect(() => {
    if (data) {
      setLoading(false);
    } else if (!queryLoading) {
      setLoading(false); // No data but query finished loading
    } else {
      setLoading(true); // Query is still loading
    }
  }, [data, queryLoading]);

  const handleSetupComplete = () => {
    setSetupComplete(true);
    setNeedsSetup(false);
    // In real app, update user/workspace setup status via API
  };

  //Show setup if user needs to complete onboarding
  if (needsSetup && !setupComplete) {
    return <SetupSteps onSetupComplete={handleSetupComplete} />;
  }

  if (loading) {
    return <DashboardLoader />;
  }

  if (!data) {
    return <div className="text-center text-secondary mt-20">Failed to load dashboard data. Please try again.</div>;
  }

  const showFinancial = data.stripeFinancialFeaturesEnabled === true || workspaceStripe;

  const { metrics } = data;

  // Transform churn signals to alerts format
  // Signals can be in signals.recent or churnSignals (for backward compatibility)
  const churnSignals = (data as any)?.signals?.recent || data.churnSignals || [];
  const recentSignals = Array.isArray(churnSignals) ? churnSignals : [];
  const activeForAlerts = recentSignals.filter((s: { status?: unknown }) => normalizeSignalStatus(s) !== 'dismissed');

  const sortedByNewest = [...activeForAlerts].sort((a: { createdAt?: string }, b: { createdAt?: string }) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    const na = Number.isFinite(ta) ? ta : 0;
    const nb = Number.isFinite(tb) ? tb : 0;
    return nb - na;
  });

  // Limit to most recent 5 signals for dashboard display (newest first)
  const limitedSignals = sortedByNewest.slice(0, 5);

  const transformedAlerts = limitedSignals.map((signal) => ({
    id: signal.id,
    title: signal.title,
    description: signal.description,
    type: (signal.priority === 'critical' ? 'critical' : signal.priority === 'high' ? 'warning' : 'info') as
      | 'critical'
      | 'warning'
      | 'info',
    timestamp: new Date(signal.createdAt).toISOString(),
    userId: signal.userId,
    userName: signal.userName,
    // Include original signal data for modal
    originalSignal: signal,
  }));

  return (
    <>
      <div>
        {/* Row 1: Timeframe picker - right aligned with Dashboard title */}
        {/* <div className="flex justify-end mb-6">
            <TimePicker
              options={TIME_PERIODS}
              selectedValue={timePeriod}
              onValueChange={setTimePeriod}
            />
          </div> */}

        {!showFinancial && (
          <div className="mb-6 rounded-lg border border-custom-lightSilver bg-custom-silver/50 px-4 py-3 text-sm text-custom-darkSilver">
            Financial metrics (MRR, subscription revenue, and related charts) are hidden until Stripe is connected with
            API access. Churn and engagement signals still use product usage data.
          </div>
        )}

        {/* Two columns: (1) metric panels + signals + charts (2) tracked activity + churn segments */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(260px,300px)] gap-4 lg:items-start">
          <div className="min-w-0 space-y-4">
            <div
              className={`grid gap-4 grid-cols-1 sm:grid-cols-2 ${showFinancial ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
            >
              {showFinancial && <MonthlyRecurringRevenue revenue={(metrics as any).mrr} />}
              <CustomCard title="Total Customers">
                <div className="text-4xl font-bold text-custom-black mb-1">
                  {(metrics.totalCustomers as any).value.toLocaleString()}
                </div>
                <div className="text-sm">
                  <span
                    className={`font-bold ${
                      metrics.totalCustomers.change.isPositive ? 'text-custom-green' : 'text-custom-red'
                    }`}
                  >
                    {(metrics.totalCustomers as any).change.percentageChange != null &&
                    (metrics.totalCustomers as any).change.percentageChangeMeaningful === true ? (
                      <>
                        {metrics.totalCustomers.change.isPositive ? '↑' : '↓'}{' '}
                        {Math.abs((metrics.totalCustomers as any).change.percentageChange ?? 0).toFixed(1)}%&nbsp;
                      </>
                    ) : (
                      <>
                        {metrics.totalCustomers.change.isPositive ? '↑' : '↓'}{' '}
                        {metrics.totalCustomers.change.value >= 0 ? '+' : ''}
                        {metrics.totalCustomers.change.value}&nbsp;
                      </>
                    )}
                  </span>
                  <span className="font-normal text-custom-darkSilver">
                    {metrics.totalCustomers.change.comparisonPeriod}
                  </span>
                </div>
                {showFinancial && (
                  <div className="mt-2 text-sm text-custom-darkSilver">
                    {(data.revenue as any)?.payingCustomers ?? 0} paying customers
                  </div>
                )}
              </CustomCard>
              <CustomCard title="Customers at Risk">
                <div className="text-4xl font-bold text-custom-black mb-1">{metrics.customersAtRisk.total}</div>
                {metrics.customersAtRisk.change.percentageChange !== 0 && (
                  <div className="text-sm">
                    <span
                      className={`font-bold ${
                        metrics.customersAtRisk.change.isPositive ? 'text-custom-red' : 'text-custom-green'
                      }`}
                    >
                      {metrics.customersAtRisk.change.isPositive ? '↑' : '↓'}{' '}
                      {Math.abs(metrics.customersAtRisk.change.percentageChange).toFixed(1)}%&nbsp;
                    </span>
                    <span className="font-normal text-custom-darkSilver">
                      {metrics.customersAtRisk.change.comparisonPeriod}
                    </span>
                  </div>
                )}
                <div className="mt-2 text-sm text-custom-darkSilver">
                  {metrics.customersAtRisk.high} high risk, {metrics.customersAtRisk.moderate} moderate
                </div>
              </CustomCard>
              <MonthlyChurnRate churnRate={metrics.churnRate} />
            </div>

            <AlertPanel alerts={transformedAlerts} />

            {showFinancial ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RevenueByPlan revenue={data.revenue || { byPlan: [] }} />
                <NetSubscriptionChange
                  subscriptionChanges={(data as any).subscriptionChanges || metrics.subscriptionChanges}
                />
              </div>
            ) : null}
          </div>

          <aside className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-4">
            <TrackedActivityPanel data={(data as DashboardData).trackedEngagement} />
            <ChurnRiskSegments
              riskSegments={(data as any)?.churn?.riskSegments}
              customersAtRisk={metrics.customersAtRisk}
              totalCustomers={(metrics.totalCustomers as any).value}
            />
          </aside>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
